// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, AppIcon, Button, Spinner, Tooltip } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { calculationResultApi } from '../../../../api/calculationResultApi';
import type { CalculationResult } from '../../../../types/calculationResult';
import type { Client } from '../../../../types/client';
import { Card } from '../../../Card';
import { MaxFundingScenarioPicker } from './MaxFundingScenarioPicker';
import { MaxFundingSaveScenarioModal } from './MaxFundingSaveScenarioModal';
import { MaxFundingDeleteScenarioModal } from './MaxFundingDeleteScenarioModal';
import { formatCurrencyEUR } from './maxFundingDefaults';
import '../../../ClientList.css';

type MaxFundingScenariosListProps = Readonly<{
  profileId: string;
  clients: Client[];
}>;

function clientLabel(client: Client | undefined): string {
  if (!client) return '—';
  const name = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim();
  return name || client.email;
}

export function MaxFundingScenariosList({ profileId, clients }: MaxFundingScenariosListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scenarios, setScenarios] = useState<CalculationResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingScenario, setEditingScenario] = useState<CalculationResult | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);
  const [saveNameError, setSaveNameError] = useState<string | null>(null);
  const [deletingScenarioId, setDeletingScenarioId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await calculationResultApi.list(profileId, 'MAXFUNDING');
      setScenarios(data);
    } catch (err) {
      console.error('Failed to load max funding scenarios:', err);
      const apiError = err as { response?: { data?: { message?: string } }; message?: string };
      setError(
        apiError.response?.data?.message ||
          apiError.message ||
          'Failed to load scenarios',
      );
    } finally {
      setIsLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    load();
  }, [load]);

  const startNewScenarioForClient = (clientId: string) => {
    setPickerOpen(false);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'tools');
    next.set('tool', 'max-funding');
    next.set('view', 'new');
    next.set('clientId', clientId);
    next.delete('scenarioId');
    setSearchParams(next, { replace: false });
  };

  const handleNewScenario = () => {
    if (clients.length === 0) {
      setError('Add at least one client to the relationship before creating a calculation.');
      return;
    }
    if (clients.length === 1) {
      startNewScenarioForClient(clients[0].id);
      return;
    }
    setPickerOpen(true);
  };

  const handleOpenScenario = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'tools');
    next.set('tool', 'max-funding');
    next.set('view', 'scenario');
    next.set('scenarioId', id);
    setSearchParams(next, { replace: false });
  };

  const handleSaveName = async (newName: string) => {
    if (!editingScenario) return;
    try {
      setIsSavingName(true);
      setSaveNameError(null);
      await calculationResultApi.update(profileId, editingScenario.id, {
        reportType: editingScenario.reportType,
        clientId: editingScenario.clientId ?? undefined,
        status: editingScenario.status,
        payload: { ...editingScenario.payload, name: newName },
      });
      setEditingScenario(null);
      await load();
    } catch (err) {
      console.error('Failed to rename scenario:', err);
      const apiError = err as { response?: { data?: { message?: string } }; message?: string };
      setSaveNameError(
        apiError.response?.data?.message || apiError.message || 'Failed to rename scenario',
      );
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDeleteSuccess = () => {
    setDeletingScenarioId(null);
    load();
  };

  const clientsById = new Map(clients.map((c) => [c.id, c] as const));

  const firstScenarioSalary = (row: CalculationResult): number | null => {
    const first = row.payload?.scenarios?.[0];
    const salary = first?.inputs?.currentGrossPayeEarnings;
    return typeof salary === 'number' && Number.isFinite(salary) ? salary : null;
  };

  const hasScenarios = !isLoading && !error && scenarios.length > 0;
  const showEmptyState = !isLoading && !error && scenarios.length === 0;

  return (
    <Card className="max-funding-scenarios-list">
      <div className="flex items-center justify-between gap-2 self-stretch py-lg px-md" data-testid="max-funding-scenarios-list">
        <div className="font-sans text-body-01 font-medium leading-normal text-black !m-0">
          Max Funding Calculator
        </div>
        <Button
          variant="filled"
          size="lg"
          onClick={handleNewScenario}
          iconLeft={<AppIcon name="plus square outline" size={24} color="#FFFFFF" />}
          data-testid="new-scenario-button"
        >
          {showEmptyState ? 'New Scenario' : 'New Calculation'}
        </Button>

      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-xxl">
          <Spinner />
        </div>
      )}

      {!isLoading && error && (
        <div className="px-md pb-lg">
          <Alert variant="error" header="Error Loading Scenarios">
            <p>{error}</p>
          </Alert>
        </div>
      )}

      {showEmptyState && (
        <div className="flex h-[100px] py-xxl mx-lg mb-lg justify-center items-center gap-[10px] self-stretch bg-lilac-06 rounded-lg" data-testid="max-funding-empty-state">
          <p className="text-[#000] font-sans text-body-02 font-normal leading-normal m-0">
            No recent activity to show
          </p>
        </div>
      )}

      {hasScenarios && (
        <table className="client-list-table overflow-x-auto">
          <thead>
            <tr>
              <th className="!p-md align-middle font-sans text-body-03 font-medium leading-normal text-black">
                Scenario Name
              </th>
              <th className="!p-md align-middle font-sans text-body-03 font-medium leading-normal text-black">
                Client
              </th>
              <th className="!p-md align-middle font-sans text-body-03 font-medium leading-normal text-black">
                Salary
              </th>
              <th className="!p-md align-middle font-sans text-body-03 font-medium leading-normal text-black">
                Created
              </th>
              <th className="!p-md align-middle font-sans text-body-03 font-medium leading-normal text-black !w-[144px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s) => {
              const client = s.clientId ? clientsById.get(s.clientId) : undefined;
              const salary = firstScenarioSalary(s);
              const scenarioName = s.payload?.name?.trim() || `Max Funding — ${clientLabel(client)}`;
              return (
                <tr
                  key={s.id}
                  className="client-list-table__row--clickable"
                  onClick={() => handleOpenScenario(s.id)}
                  data-testid={`scenario-card-${s.id}`}
                >
                  <td>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-black font-sans text-body-03 font-medium leading-normal">
                      {scenarioName}
                    </span>
                  </td>
                  <td>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-black font-sans text-body-03 font-normal leading-normal">
                      {clientLabel(client)}
                    </span>
                  </td>
                  <td>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-black font-sans text-body-03 font-normal leading-normal">
                      {salary == null ? '—' : formatCurrencyEUR(salary).replace('.00', '')}
                    </span>
                  </td>
                  <td>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-black font-sans text-body-03 font-normal leading-normal">
                      {formatDate(s.createdAt)}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-md">
                      <Tooltip
                        content="Edit scenario name"
                        trigger={
                          <button
                            type="button"
                            className="flex w-[32px] h-[32px] p-sm justify-center items-center shrink-0 rounded-full bg-lilac-05 border-none cursor-pointer"
                            onClick={() => setEditingScenario(s)}
                            title="Edit scenario name"
                            aria-label={`Edit name of ${scenarioName}`}
                            data-testid={`scenario-edit-${s.id}`}
                          >
                            <AppIcon name="edit" size={16} color="#2D0A6C" />
                          </button>
                        }
                      />
                      <Tooltip
                        content="Delete scenario"
                        trigger={
                          <button
                            type="button"
                            className="flex w-[32px] h-[32px] p-sm justify-center items-center shrink-0 rounded-full bg-lilac-05 border-none cursor-pointer"
                            onClick={() => setDeletingScenarioId(s.id)}
                            title="Delete scenario"
                            aria-label={`Delete ${scenarioName}`}
                            data-testid={`scenario-delete-${s.id}`}
                          >
                            <AppIcon name="trash" size={16} color="#2D0A6C" />
                          </button>
                        }
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pickerOpen && (
        <MaxFundingScenarioPicker
          open
          clients={clients}
          creating={false}
          onClose={() => setPickerOpen(false)}
          onCreate={startNewScenarioForClient}
        />
      )}

      {editingScenario && (
        <MaxFundingSaveScenarioModal
          open
          defaultName={editingScenario.payload?.name ?? ''}
          saving={isSavingName}
          error={saveNameError}
          title="Edit Scenario Name"
          primaryActionLabel="Save Name"
          headerIcon="edit"
          onClose={() => {
            setEditingScenario(null);
            setSaveNameError(null);
          }}
          onSave={handleSaveName}
        />
      )}

      {deletingScenarioId && (
        <MaxFundingDeleteScenarioModal
          open
          profileId={profileId}
          scenarioId={deletingScenarioId}
          onClose={() => setDeletingScenarioId(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </Card>
  );
}
